import { useState, useEffect } from 'react';

import { AppHeader } from '@/components/layout/AppHeader';
import { AppSidebar } from '@/components/layout/AppSidebar';
import { useWorkspace } from '@/lib/contexts/useWorkspace';
import { CreateProjectModal } from '@/components/project/CreateProjectModal';
import { projectService, PROJECT_TYPES, type Project } from '@/lib/services/projectService';
import { createAuthenticatedUrl } from '@bri/shared-components';
import { 
  PlusIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';

import toast from 'react-hot-toast';
import { formatDateTime, formatRelativeTime, getErrorMessage } from '@/lib/utils';

// Get the Qual Analyzer URL from environment or use default
const QUAL_ANALYZER_URL = import.meta.env.VITE_QUAL_ANALYZER_URL || 'http://localhost:3001';



function ProjectsList() {
  const [searchTerm, setSearchTerm] = useState('');
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const { currentWorkspace } = useWorkspace();

  // Load projects from the insights objects service
  useEffect(() => {
    const loadProjects = async () => {
      if (!currentWorkspace) {
        setProjects([]);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const projectsData = await projectService.listProjects(currentWorkspace.workspaceId);
        setProjects(projectsData);
      } catch (error: unknown) {
        console.error('Failed to load projects:', error);
        toast.error(getErrorMessage(error) || 'Failed to load projects');
        setProjects([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadProjects();
  }, [currentWorkspace]);

  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (project.description || '').toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const handleProjectCreated = (newProject: Project) => {
    setProjects(prev => [newProject, ...prev]);
  };

  const getProjectTypeInfo = (projectType: string) => {
    return PROJECT_TYPES.find(type => type.id === projectType) || PROJECT_TYPES[0];
  };



  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header - full width at top */}
      <AppHeader />
      
      {/* Content area with sidebar */}
      <div className="flex flex-1">
        <AppSidebar />
        
        {/* Main content */}
        <main className="flex-1 py-8">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            {/* Header */}
            <div className="mb-8">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">Projects</h1>
                  <p className="mt-2 text-gray-600">
                    {currentWorkspace
                      ? `${currentWorkspace.name} • ${projects.length} projects in the active workspace`
                      : 'Projects belong to your active workspace. Switch workspaces from the header when needed.'}
                  </p>
                </div>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="bg-primary text-primary-foreground px-4 py-2 rounded-lg font-medium hover:bg-primary/90 transition-colors inline-flex items-center"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  New Project
                </button>
              </div>
            </div>

            {/* Search */}
            <div className="mb-6">
              <div className="relative max-w-md">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search projects..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            {/* Projects Grid */}
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {isLoading ? (
                <div className="col-span-full flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="loading-spinner w-8 h-8 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading projects...</p>
                  </div>
                </div>
              ) : filteredProjects.length === 0 ? (
                <div className="col-span-full text-center py-12">
                  <div className="mx-auto h-12 w-12 text-gray-400">
                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <h3 className="mt-4 text-lg font-medium text-gray-900">No projects yet</h3>
                  <p className="mt-2 text-gray-500">
                    {currentWorkspace
                      ? `Create the first project inside ${currentWorkspace.name}.`
                      : 'Create the first project in your active workspace.'}
                  </p>
                  <div className="mt-6">
                    <button
                      onClick={() => setShowCreateModal(true)}
                      className="bg-primary text-primary-foreground px-4 py-2 rounded-lg font-medium hover:bg-primary/90 transition-colors inline-flex items-center"
                    >
                      <PlusIcon className="h-5 w-5 mr-2" />
                      Create Your First Project
                    </button>
                  </div>
                </div>
              ) : (
                filteredProjects.map((project) => {
                  const typeInfo = getProjectTypeInfo(project.projectType);
                  return (
                    <div key={project.id} className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow">
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="text-2xl">{typeInfo.icon}</div>
                        <div>
                          <h3 className="font-semibold text-gray-900 text-lg">{project.name}</h3>
                          <p className="text-sm text-gray-500">{typeInfo.name}</p>
                        </div>
                      </div>
                      
                      {project.description && (
                        <p className="text-gray-600 text-sm mb-4 line-clamp-2">{project.description}</p>
                      )}
                      
                      {/* Project Details */}
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-gray-500">Created:</span>
                          <span className="text-gray-900">
                            <span title={formatDateTime(project.createdDate)}>
                              {formatRelativeTime(project.createdDate)}
                            </span>
                          </span>
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center pt-4 border-t border-gray-100">
                        <a 
                          href={project.projectType === 'qualitative-analyzer' 
                            ? createAuthenticatedUrl(`${QUAL_ANALYZER_URL}/project/${project.id}`)
                            : `/app/projects/${project.id}`}
                          className="text-sm text-primary hover:text-primary/80 font-medium"
                        >
                          Open Project
                        </a>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            
            {/* Create Project Modal */}
            <CreateProjectModal
              open={showCreateModal}
              onOpenChange={setShowCreateModal}
              onProjectCreated={handleProjectCreated}
            />
          </div>
        </main>
      </div>
    </div>
  );
}



export default function ProjectsPage() {
  return <ProjectsList />;
}
